import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
export const ACCOUNT_HELP_LOGIN = '/login?login=true&next=%2Faccount-help';

// The guide remains readable when a visitor's session cannot be loaded.
export default function AccountHelp({
  user
}) {
  const [topic, setTopic] = useState('');
  const [outcome, setOutcome] = useState('');
  const [attempted, setAttempted] = useState('');
  const [status, setStatus] = useState('idle');
  const pending = useRef(false);
  const registered = user?.isRegistered === true;
  const submit = async event => {
    event.preventDefault();
    if (pending.current || !registered || !topic || !outcome || !attempted) return;
    pending.current = true;
    setStatus('pending');
    try {
      const response = await fetch('/api/account-help/feedback', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          topic,
          outcome,
          attempted_steps: attempted === 'yes'
        })
      });
      setStatus(response.status === 204 ? 'saved' : response.status === 401 ? 'signed_out' : response.status === 429 ? 'limited' : 'error');
    } catch (_) {
      setStatus('error');
    } finally {
      pending.current = false;
    }
  };
  return /*#__PURE__*/React.createElement("main", {
    className: "account-help"
  }, /*#__PURE__*/React.createElement(Helmet, null, /*#__PURE__*/React.createElement("title", null, "Sign-in and Team Manager Help | TeamBridge"), /*#__PURE__*/React.createElement("meta", {
    name: "description",
    content: "Help with TeamBridge sign-in, password recovery and owner-controlled team manager access."
  }), /*#__PURE__*/React.createElement("link", {
    rel: "canonical",
    href: "https://teambridge.app/account-help"
  })), /*#__PURE__*/React.createElement("style", null, `
        .account-help { color:#17243b; background:#fff; max-width:760px; margin:0 auto; padding:28px 20px 60px; font:18px/1.6 system-ui,sans-serif; overflow-wrap:anywhere; }
        .account-help * { box-sizing:border-box; }
        .account-help h1 { font-size:clamp(30px,6vw,44px); line-height:1.2; margin:30px 0 16px; }
        .account-help h2 { font-size:26px; line-height:1.3; margin:36px 0 12px; }
        .account-help h3 { font-size:21px; margin-top:24px; }
        .account-help a { color:#174ca6; text-decoration:underline; }
        .account-help a:focus-visible, .account-help select:focus-visible, .account-help button:focus-visible { outline:3px solid #174ca6; outline-offset:4px; }
        .account-help nav { display:flex; flex-wrap:wrap; gap:12px 24px; }
        .account-help li { margin:10px 0; }
        .account-help form { background:#f3f6fa; padding:20px; border-radius:12px; }
        .account-help label { display:block; font-weight:600; margin-top:16px; }
        .account-help select { display:block; width:100%; min-height:46px; margin:6px 0 16px; padding:8px; color:#17243b; background:white; border:1px solid #6b7280; border-radius:4px; font:inherit; }
        .account-help button { min-height:46px; padding:10px 20px; border:0; border-radius:6px; color:white; background:#174ca6; font:inherit; cursor:pointer; }
        .account-help button:disabled { opacity:.6; cursor:default; }
        .account-help .note { border-left:4px solid #6b7280; padding:12px 16px; background:#f3f6fa; }
        .account-help footer { border-top:1px solid #d5dce5; margin-top:40px; padding-top:20px; }
      `), /*#__PURE__*/React.createElement("nav", {
    "aria-label": "Site"
  }, /*#__PURE__*/React.createElement("a", {
    href: "/"
  }, "TeamBridge"), /*#__PURE__*/React.createElement("a", {
    href: ACCOUNT_HELP_LOGIN
  }, "Log in")), /*#__PURE__*/React.createElement("h1", null, "Sign-in and team manager help"), /*#__PURE__*/React.createElement("p", null, "Find the steps for your account or team. You can read this guide without signing in."), /*#__PURE__*/React.createElement("nav", {
    "aria-label": "On this page"
  }, /*#__PURE__*/React.createElement("a", {
    href: "#sign-in"
  }, "Sign in"), /*#__PURE__*/React.createElement("a", {
    href: "#password"
  }, "Reset a password"), /*#__PURE__*/React.createElement("a", {
    href: "#team-management"
  }, "Team manager access")), /*#__PURE__*/React.createElement("section", {
    "aria-labelledby": "sign-in"
  }, /*#__PURE__*/React.createElement("h2", {
    id: "sign-in"
  }, "Sign in to your account"), /*#__PURE__*/React.createElement("ol", null, /*#__PURE__*/React.createElement("li", null, /*#__PURE__*/React.createElement("a", {
    href: ACCOUNT_HELP_LOGIN
  }, "Open the login page"), ". After a successful login, this link returns you to this guide."), /*#__PURE__*/React.createElement("li", null, "If you signed up with Google, choose ", /*#__PURE__*/React.createElement("strong", null, "Log in with Google"), " and use the same Google account."), /*#__PURE__*/React.createElement("li", null, "If you signed up with email and a password, enter that email and password, then choose ", /*#__PURE__*/React.createElement("strong", null, "Log in"), ".")), /*#__PURE__*/React.createElement("p", {
    className: "note"
  }, "If your company requires Okta or another enterprise SSO provider, ask your IT administrator which sign-in methods your policy permits. This guide does not provide an Okta setup or an implementation date. Do not work around your company\u2019s access policy.")), /*#__PURE__*/React.createElement("section", {
    "aria-labelledby": "password"
  }, /*#__PURE__*/React.createElement("h2", {
    id: "password"
  }, "Request a password reset"), /*#__PURE__*/React.createElement("ol", null, /*#__PURE__*/React.createElement("li", null, "On the ", /*#__PURE__*/React.createElement("a", {
    href: ACCOUNT_HELP_LOGIN
  }, "login page"), ", choose ", /*#__PURE__*/React.createElement("strong", null, "Forgot password?"), "."), /*#__PURE__*/React.createElement("li", null, "Enter your account email and submit the request."), /*#__PURE__*/React.createElement("li", null, "If an account matches, look for reset instructions in your inbox and spam folder. Follow the email link to choose a new password.")), /*#__PURE__*/React.createElement("p", null, "A request confirmation does not confirm email delivery. If a reset link has expired, request a new one. Google users should first try the same Google account they used to sign up."), /*#__PURE__*/React.createElement("p", null, "If you still cannot sign in, ", /*#__PURE__*/React.createElement("a", {
    href: "mailto:rob@teambridge.app"
  }, "contact support"), ". Never send your password or a password-reset link.")), /*#__PURE__*/React.createElement("section", {
    "aria-labelledby": "team-management"
  }, /*#__PURE__*/React.createElement("h2", {
    id: "team-management"
  }, "Give a teammate manager access"), /*#__PURE__*/React.createElement("p", null, "The current team owner can delegate everyday team administration to an existing registered member."), /*#__PURE__*/React.createElement("ol", null, /*#__PURE__*/React.createElement("li", null, "The owner signs in and opens the ", /*#__PURE__*/React.createElement("a", {
    href: "/team"
  }, "Team page"), "."), /*#__PURE__*/React.createElement("li", null, "The intended manager must already be a team member with a registered account."), /*#__PURE__*/React.createElement("li", null, "The owner chooses ", /*#__PURE__*/React.createElement("strong", null, "Manage"), " beside that member, then ", /*#__PURE__*/React.createElement("strong", null, "Make team manager"), "."), /*#__PURE__*/React.createElement("li", null, "To revoke the role later, the owner chooses ", /*#__PURE__*/React.createElement("strong", null, "Manage"), ", then ", /*#__PURE__*/React.createElement("strong", null, "Remove manager access"), ".")), /*#__PURE__*/React.createElement("h3", null, "What a manager can do"), /*#__PURE__*/React.createElement("p", null, "Managers can edit team details and remove ordinary members. They cannot remove the owner or other managers, assign manager roles, change team billing or subscriptions, transfer ownership, or change member limits."), /*#__PURE__*/React.createElement("p", null, "Granting or removing manager access leaves the team\u2019s ownership, billing, subscription and limits unchanged. If the current owner is unavailable or you need ownership or billing changes, ", /*#__PURE__*/React.createElement("a", {
    href: "mailto:rob@teambridge.app"
  }, "contact support"), " to explain the situation. Manager access alone does not solve those requests.")), /*#__PURE__*/React.createElement("section", {
    "aria-labelledby": "help-feedback"
  }, /*#__PURE__*/React.createElement("h2", {
    id: "help-feedback"
  }, "Did these steps help?"), /*#__PURE__*/React.createElement("p", null, "Feedback is optional. Your answers are saved with your TeamBridge account to help us improve this guide. There is no need to share passwords or private details."), !registered ? /*#__PURE__*/React.createElement("p", null, /*#__PURE__*/React.createElement("a", {
    href: ACCOUNT_HELP_LOGIN
  }, "Log in to leave feedback"), ". You can keep using the guide without doing so.") : status === 'saved' ? /*#__PURE__*/React.createElement("p", {
    role: "status"
  }, "Thank you. Your feedback has been saved.") : /*#__PURE__*/React.createElement("form", {
    onSubmit: submit,
    "aria-busy": status === 'pending'
  }, /*#__PURE__*/React.createElement("label", {
    htmlFor: "help-topic"
  }, "What were you trying to do?"), /*#__PURE__*/React.createElement("select", {
    id: "help-topic",
    value: topic,
    onChange: e => setTopic(e.target.value),
    required: true,
    disabled: status === 'pending'
  }, /*#__PURE__*/React.createElement("option", {
    value: "",
    disabled: true
  }, "Choose a topic"), /*#__PURE__*/React.createElement("option", {
    value: "sign_in"
  }, "Sign in to my account"), /*#__PURE__*/React.createElement("option", {
    value: "team_management"
  }, "Manage my team\u2019s everyday tasks")), /*#__PURE__*/React.createElement("label", {
    htmlFor: "help-attempted"
  }, "Did you try the relevant steps?"), /*#__PURE__*/React.createElement("select", {
    id: "help-attempted",
    value: attempted,
    onChange: e => setAttempted(e.target.value),
    required: true,
    disabled: status === 'pending'
  }, /*#__PURE__*/React.createElement("option", {
    value: "",
    disabled: true
  }, "Choose an answer"), /*#__PURE__*/React.createElement("option", {
    value: "yes"
  }, "Yes"), /*#__PURE__*/React.createElement("option", {
    value: "no"
  }, "No")), /*#__PURE__*/React.createElement("label", {
    htmlFor: "help-outcome"
  }, "Did this guide help you solve that problem?"), /*#__PURE__*/React.createElement("select", {
    id: "help-outcome",
    value: outcome,
    onChange: e => setOutcome(e.target.value),
    required: true,
    disabled: status === 'pending'
  }, /*#__PURE__*/React.createElement("option", {
    value: "",
    disabled: true
  }, "Choose an answer"), /*#__PURE__*/React.createElement("option", {
    value: "resolved"
  }, "Yes"), /*#__PURE__*/React.createElement("option", {
    value: "unresolved"
  }, "No"), /*#__PURE__*/React.createElement("option", {
    value: "unsure"
  }, "I\u2019m not sure")), status === 'signed_out' && /*#__PURE__*/React.createElement("p", {
    role: "alert"
  }, "Your session could not be verified. ", /*#__PURE__*/React.createElement("a", {
    href: ACCOUNT_HELP_LOGIN
  }, "Log in again"), " to send feedback."), status === 'limited' && /*#__PURE__*/React.createElement("p", {
    role: "status"
  }, "Feedback has already been received from this account today. You can send another response after 00:00 UTC."), status === 'error' && /*#__PURE__*/React.createElement("p", {
    role: "alert"
  }, "We couldn\u2019t save your feedback. Please try again."), /*#__PURE__*/React.createElement("button", {
    type: "submit",
    disabled: status === 'pending' || status === 'limited'
  }, status === 'pending' ? 'Sending…' : 'Send feedback'))), /*#__PURE__*/React.createElement("footer", null, /*#__PURE__*/React.createElement("a", {
    href: "/privacy"
  }, "Privacy policy"), " \xB7 ", /*#__PURE__*/React.createElement("a", {
    href: "mailto:rob@teambridge.app"
  }, "Contact support")));
}